import React from "react";
import { formatDurasi } from "@/lib/timer";
import { TopBar } from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { apiRiwayatProduksi, apiRiwayatProduksiDetail, apiStokReworkUlangi, formatApiErrorDetail, mockupUrl } from "@/lib/api";
import { ringkasCeklis, nilaiButir, labelData } from "@/lib/riwayat";
import SelisihQty from "@/components/SelisihQty";
import { rencanaTeks, qty } from "@/lib/format";
import { Loader2, RefreshCw, Search, AlertTriangle, X } from "lucide-react";
import * as W from "@/lib/waktu";
import { namaPos } from "@/lib/posNama";
import usePetaPos from "@/lib/usePetaPos";

// Riwayat Produksi (F1.5-J, Addendum 1 point 4): one card per SPK of every
// door with a progress line up to PENGAMBILAN. The server decides what each
// role may see (CS: no payment status, no PIC); this page only shows it.
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const inputCls = "rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
const JENIS = [["", "Semua"], ["DIGITAL_PRINT", "Digital Printing"], ["JERSEY", "Jersey"], ["DTF", "DTF"], ["CUSTOM", "Custom"]];
const STATUS = ["MENUNGGU", "PROSES", "SELESAI", "DIAMBIL", "BATAL"];
const STATUS_CLS = { MENUNGGU: "bg-slate-700 text-slate-200", PROSES: "bg-sky-500/20 text-sky-300", SELESAI: "bg-emerald-500/20 text-emerald-300",
  DIAMBIL: "bg-emerald-700/40 text-emerald-200", BATAL: "bg-red-500/20 text-red-300" };
const STEP_CLS = { SELESAI: "bg-emerald-500/25 border-emerald-500/50 text-emerald-200", PROSES: "bg-sky-500/25 border-sky-500/50 text-sky-200",
  BELUM: "bg-slate-800 border-slate-700 text-slate-500" };
const BAYAR_CLS = { BELUM_BAYAR: "text-red-300", DP: "text-amber-300", LUNAS: "text-emerald-300" };
const waktu = (iso) => (iso ? W.waktu(iso) : "—");
const durasi = (m) => (m == null ? "" : m < 60 ? `${m} mnt` : `${Math.floor(m / 60)} j ${m % 60} mnt`);

function Progres({ steps }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {steps.map((s, i) => (
        <span key={`${s.pos_code}-${i}`} className={`rounded border px-1.5 py-0.5 text-[11px] font-mono ${STEP_CLS[s.status] || STEP_CLS.BELUM}`}>{s.pos_nama}</span>
      ))}
    </div>
  );
}

const Kotak = ({ label, nilai, testid }) => (
  <div data-testid={testid} className="rounded-md border border-slate-700 bg-slate-950/60 px-2.5 py-1.5">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-mono text-sm text-slate-100">{nilai ?? "—"}</div>
  </div>
);

// G1-F: quantities of a station (Press per component, others in / result / reject)
function QtyIsi({ qty }) {
  if (!qty) return null;
  const satuan = qty.satuan?.toLowerCase();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="riwayat-qty">
      {qty.masuk != null && <Kotak label={`Masuk (${qty.masuk_satuan?.toLowerCase() || satuan})`} nilai={qty.masuk} />}
      <Kotak label={`Hasil (${satuan})`} nilai={qty.hasil} />
      <Kotak label={`Reject (${satuan})`} nilai={qty.reject} />
      {qty.komponen && Object.entries(qty.komponen).map(([k, v]) => (
        <Kotak key={k} label={k} nilai={`${v.hasil}${v.reject ? ` · reject ${v.reject}` : ""}`} />
      ))}
    </div>
  );
}

// G1-F: every checklist item with its value (template version of that step)
function Ceklis({ c, judul = "Checklist" }) {
  if (!c) return null;
  const r = ringkasCeklis(c);
  return (
    <div className="space-y-1.5" data-testid="riwayat-checklist">
      <div className="text-xs">
        <span className="text-slate-400">{judul}: </span>
        <span data-testid="riwayat-checklist-ringkas" className={r.lengkap ? "text-emerald-300 font-semibold" : "text-amber-300 font-semibold"}>✓ {r.teks}</span>
        {c.oleh && <span className="text-slate-500"> · diisi {c.oleh} {waktu(c.waktu)}</span>}
      </div>
      {c.terisi && c.butir.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {c.butir.map((b, i) => (
            <div key={i} className={`flex items-center justify-between gap-2 rounded border px-2 py-1 text-xs ${b.ok ? "border-slate-700 bg-slate-950/60" : b.wajib ? "border-red-500/40 bg-red-500/10" : "border-slate-800 bg-slate-950/30"}`}>
              <span className="text-slate-300">{b.label}{b.wajib ? "" : <span className="text-slate-600"> (opsional)</span>}</span>
              <span className={`font-mono ${b.tipe === "TEKS" ? "text-slate-100" : b.ok ? "text-emerald-300" : "text-slate-500"}`}>{nilaiButir(b)}</span>
            </div>
          ))}
        </div>
      )}
      {c.kurang?.length > 0 && <div className="text-xs text-amber-300">Belum lengkap: {c.kurang.join(", ")} — alasan: {c.alasan_kurang}</div>}
    </div>
  );
}

function AccIsi({ t }) {
  const a = t.acc;
  return (
    <div className="space-y-2" data-testid="riwayat-acc">
      {a ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Kotak label="Status" nilai={a.status === "DISETUJUI" ? "Disetujui" : a.status === "REVISI" ? "Minta revisi" : a.status} />
          <Kotak label="Lewat" nilai={a.sumber === "WHATSAPP" ? "WhatsApp (dicatat kasir)" : a.sumber === "HALAMAN" ? "Halaman ACC customer" : a.sumber} />
          {a.nama && <Kotak label="PIC ACC" nilai={a.nama} testid="riwayat-acc-pic" />}
          <Kotak label="Tanggal ACC" nilai={a.tanggal || waktu(a.waktu)} />
          {a.catatan && <Kotak label="Bukti / catatan" nilai={a.catatan} />}
          <Kotak label="Versi mockup" nilai={a.versi_mockup || "—"} />
        </div>
      ) : <p className="text-xs text-slate-500">Belum ada ACC customer.</p>}
      <Ceklis c={t.checklist} judul="Checklist ACC" />
      {t.checklist_jersey && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {t.checklist_jersey.map((b, i) => (
            <div key={i} className="flex items-center justify-between rounded border border-slate-700 bg-slate-950/60 px-2 py-1 text-xs">
              <span className="text-slate-300">{b.label}</span>
              <span className={b.ok ? "text-emerald-300" : "text-slate-500"}>{b.ok ? `✓ ${b.oleh || ""}` : "—"}</span>
            </div>
          ))}
        </div>
      )}
      {t.acc_final?.map((f, i) => <div key={i} className="text-xs text-emerald-300">ACC final {f.revisi_ke ? `(revisi ${f.revisi_ke}) ` : ""}oleh {f.oleh || "—"} · {waktu(f.waktu)}</div>)}
      {t.riwayat?.filter((r) => r.status === "REVISI").map((r, i) => <div key={i} className="text-xs text-amber-300">Customer minta revisi {waktu(r.waktu)}{r.catatan ? `: ${r.catatan}` : ""}</div>)}
    </div>
  );
}

function Detail({ spkId, onClose }) {
  const { user } = useAuth();
  const petaPos = usePetaPos();  // v2.17.2: station names, not codes
  const isOwner = user?.role === "admin";
  const [d, setD] = React.useState(null);
  const [error, setError] = React.useState(null);
  React.useEffect(() => { apiRiwayatProduksiDetail(spkId).then(setD).catch((e) => setError(errText(e))); }, [spkId]);
  return (
    <div data-testid="riwayat-detail" className="rounded-xl border border-amber-500/30 bg-slate-900 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        {d ? (
          <div className="space-y-1">
            <div className="font-mono text-amber-300">{d.nomor_spk} · {d.nomor_faktur}</div>
            <div className="text-sm text-slate-300">{d.customer}{d.jersey?.tim ? ` · ${d.jersey.tim}` : ""}{d.jersey?.pic ? ` · PIC ${d.jersey.pic}` : ""}{d.jersey?.total_pcs ? ` · ${d.jersey.total_pcs} pcs` : ""}</div>
            <div className="text-xs text-slate-400">SPK {waktu(d.tanggal_spk)} · rencana selesai {rencanaTeks(d)} · selesai {d.selesai_aktual ? waktu(d.selesai_aktual) : "—"}
              {d.status_bayar && <span className={`ml-2 ${BAYAR_CLS[d.status_bayar] || ""}`}>{d.status_bayar}</span>}
              <span className={`ml-2 rounded px-2 py-0.5 text-[11px] font-mono ${STATUS_CLS[d.status] || ""}`}>STATUS TERAKHIR: {d.status}</span></div>
            {d.progres && <div data-testid="riwayat-detail-progres"><Progres steps={d.progres} /></div>}
          </div>
        ) : <span className="text-slate-400">{error || <Loader2 size={16} className="animate-spin" />}</span>}
        <button data-testid="riwayat-detail-tutup" onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={18} /></button>
      </div>
      {d?.stok_rework?.length > 0 && (
        <div data-testid="riwayat-stok-rework" className="rounded border border-slate-700 bg-slate-950/60 p-2 text-xs text-slate-300 space-y-1">
          <div className="font-semibold">Bahan rework (terbuang karena reject QC)</div>
          {d.stok_rework.map((r) => (
            <div key={r.rework_ke}>Rework ke-{r.rework_ke}: {r.status === "OK" ? (r.bahan.map((b) => `${b.kode} ${qty(b.qty_milli)} ${b.satuan || ""}`).join(", ") || "—")
              : r.status === "TANPA_BAHAN" ? "tidak ada bahan (BOM kosong / pos belum diisi)" : <span className="text-red-300">GAGAL: {r.gagal}</span>}
              {r.tanpa_pos?.length > 0 && <span className="text-amber-300"> · tanpa pos: {r.tanpa_pos.map((t) => t.kode).join(", ")}</span>}
              {r.status === "GAGAL" && isOwner && <button data-testid={`riwayat-stok-rework-ulangi-${r.rework_ke}`} onClick={() => apiStokReworkUlangi(spkId, r.rework_ke).then(() => apiRiwayatProduksiDetail(spkId).then(setD)).catch((e) => setError(errText(e)))} className="ml-2 underline text-cyan-300">ulangi</button>}
            </div>))}
        </div>
      )}
      {d?.selisih_qty?.length > 0 && (
        <div data-testid="riwayat-detail-selisih" className="rounded border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-200">
          <div className="flex items-center gap-1 font-semibold"><AlertTriangle size={13} /> Selisih qty</div>
          {d.selisih_qty.map((x, i) => <div key={i}>{x.pos_nama || x.pos_code}: masuk {x.masuk}, hasil {x.hasil}, reject {x.reject} → hilang {x.hilang} {x.satuan?.toLowerCase()} ({x.oleh?.username ? `${x.oleh.username}, ` : ""}{waktu(x.waktu)})</div>)}
        </div>
      )}
      {d && (
        <ol className="space-y-3">
          {d.tahap.map((t, i) => (
            <li key={`${t.pos_code}-${i}`} data-testid={`riwayat-tahap-${t.pos_code}`} className="rounded-lg border border-slate-800 bg-slate-950/40 overflow-hidden">
              <div className={`flex flex-wrap items-center gap-2 px-3 py-2 border-b ${STEP_CLS[t.status] || STEP_CLS.BELUM}`}>
                <span className="font-semibold text-sm">{t.pos_nama || t.pos_code}</span>
                <span className="text-[11px] font-mono">{t.status}</span>
                {t.durasi_detik != null ? <span data-testid={`riwayat-durasi-${t.pos_code}`} className="text-xs font-mono tabular-nums">{formatDurasi(t.durasi_detik)}</span>
                  : t.durasi_menit != null && <span className="text-xs">{durasi(t.durasi_menit)}</span>}
                <span data-testid={`riwayat-label-${t.pos_code}`} className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-mono ${t.ada_data ? "bg-emerald-500/20 text-emerald-200" : "bg-slate-800 text-slate-400"}`}>{labelData(t)}</span>
              </div>
              <div className="p-3 space-y-2">
                {t.pos_code === "ACC_CUSTOMER" ? <AccIsi t={t} /> : (
                  <>
                    {t.ada_data || t.mulai ? (
                      <div className="text-xs text-slate-300">
                        {t.pos_code === "PENGAMBILAN"
                          ? <>{t.cara === "DIKIRIM" ? "Dikirim" : "Diambil"} {waktu(t.selesai)} · diserahkan {t.oleh_selesai || "—"}{t.penerima ? ` · penerima ${t.penerima}` : ""}</>
                          : <>Mulai {waktu(t.mulai)} ({t.oleh_mulai || "—"}) · selesai {waktu(t.selesai)} ({t.oleh_selesai || "—"})</>}
                      </div>
                    ) : <p className="text-xs text-slate-500">Belum ada data yang tersimpan untuk tahap ini.</p>}
                    {t.bagian?.length > 0 && (
                      <table data-testid={`riwayat-bagian-${t.pos_code}`} className="w-full text-xs text-slate-300">
                        <thead><tr className="text-left text-slate-500"><th>Putaran</th><th>Penjahit</th><th className="text-right">Dibagi</th><th className="text-right">Hasil</th><th className="text-right">Reject</th><th className="pl-2">Checklist</th><th className="pl-2">Selesai</th></tr></thead>
                        <tbody>{t.bagian.map((b, j) => (
                          <tr key={`b${j}`} className="border-t border-slate-800/60"><td>{b.putaran ? `rework ke-${b.putaran}` : "awal"}</td><td>{b.operator}</td>
                            <td className="text-right">{b.pcs}</td><td className="text-right">{b.hasil ?? "—"}</td><td className="text-right">{b.reject ?? "—"}</td>
                            <td className={`pl-2 ${b.checklist && b.checklist.ok < b.checklist.total ? "text-amber-300" : "text-emerald-300"}`} title={b.checklist?.alasan_kurang || ""}>{b.checklist ? `${b.checklist.ok}/${b.checklist.total}` : "—"}</td>
                            <td className="pl-2">{b.selesai ? waktu(b.selesai) : b.status}</td></tr>))}</tbody>
                      </table>
                    )}
                    {t.pembagian?.map((p, j) => (
                      <div key={`pb${j}`} className="text-xs text-cyan-200">Pembagian {p.sumber === "OWNER" ? "diubah Owner" : p.sumber === "QC" ? "rework (QC)" : "oleh Press"} {waktu(p.waktu)} ({p.oleh}){p.mode === "ATUR" || p.sumber === "OWNER" ? ` — ${p.alasan || ""}` : ""}: {p.alokasi.map((a) => `${a.operator} ${a.pcs}`).join(", ")}</div>
                    ))}
                    {t.alih_riwayat?.map((a, j) => (
                      <div key={`a${j}`} data-testid={`riwayat-alih-${t.pos_code}`} className="text-xs text-amber-200">Dialihkan {waktu(a.waktu)} dari {a.dari?.username || "—"} ke {a.ke?.username} oleh {a.oleh?.username} — {a.alasan}</div>
                    ))}
                    {t.penyebab_reject?.length > 0 && t.penyebab_reject.map((p, j) => (
                      <div key={`p${j}`} data-testid={`riwayat-penyebab-${t.pos_code}`} className="text-xs font-semibold text-red-300">⚠ Penyebab {p.sumber === "POS" ? "lapor masalah" : "reject QC"} {waktu(p.qc_at)}{p.qty_reject ? ` · ${p.qty_reject} pcs` : ""}{p.rework_ke ? ` (rework ke-${p.rework_ke})` : ""}</div>
                    ))}
                    {t.upah_rework?.map((u, j) => (
                      <div key={`u${j}`} data-testid={`riwayat-upah-rework-${u.rework_ke}`} className={`text-xs ${u.dibayar ? "text-emerald-300" : "text-red-300"}`}>Upah rework ke-{u.rework_ke}: {u.dibayar ? "dibayar" : "tidak dibayar (penyebab pos ini)"}</div>
                    ))}
                    {t.catatan && <div className="text-xs text-slate-400">Catatan: {t.catatan}</div>}
                    <QtyIsi qty={t.form?.qty} />
                    {t.checklist && (t.checklist.total > 0 || t.checklist.terisi) && <Ceklis c={t.checklist} />}
                    {t.pos_code === "DESAIN" && d.mockup && (
                      <div data-testid="riwayat-mockup" className="space-y-1">
                        <div className="text-xs text-slate-400">Mockup desain (versi {d.mockup.versi})</div>
                        <a href={mockupUrl(d.order_id, d.mockup.versi)} target="_blank" rel="noopener noreferrer">
                          <img src={mockupUrl(d.order_id, d.mockup.versi)} alt={`Mockup versi ${d.mockup.versi}`} className="max-h-72 rounded border border-slate-700" />
                        </a>
                      </div>
                    )}
                    {t.rework?.length > 0 && t.rework.map((r, j) => (
                      <div key={j} className="text-xs text-amber-300">Rework{r.rework_ke ? ` ke-${r.rework_ke}` : ""} dari {r.sumber === "POS" ? `lapor masalah ${namaPos(r.pos_asal, petaPos, r.pos_asal_nama)} (${r.qc_by_username})` : "QC"} {waktu(r.qc_at)}: {r.reason}{r.sumber === "POS" && r.item ? ` · ${r.item}` : ""}{r.pcs_bermasalah ? ` · ${r.pcs_bermasalah}` : ""}{r.qty_reject ? ` · ${r.qty_reject} pcs` : ""}
                        {r.pos_penyebab && r.pos_penyebab !== t.pos_code ? <span className="text-slate-400"> · penyebab {namaPos(r.pos_penyebab, petaPos)} — pekerjaan rework, bukan kesalahan pos ini</span> : null}</div>
                    ))}
                    {/* v2.17: problems this station reported back to an earlier one */}
                    {t.lapor_masalah?.length > 0 && t.lapor_masalah.map((r, j) => (
                      <div key={`lm${j}`} data-testid={`riwayat-lapor-${t.pos_code}`} className="text-xs text-orange-700">Lapor masalah ke {namaPos(r.pos_rework, petaPos, r.pos_rework_nama)} {waktu(r.qc_at)} ({r.qc_by_username}): {r.reason} · {r.item ? `${r.item} ` : ""}{r.qty_reject} {(r.satuan || "pcs").toLowerCase()}</div>
                    ))}
                    {t.putaran_qc?.length > 0 && (
                      <ul data-testid="riwayat-putaran-qc" className="text-xs text-slate-300 space-y-0.5">
                        {t.putaran_qc.map((p, j) => (
                          <li key={j}>{p.rework_ke ? `Rework ke-${p.rework_ke}` : "Putaran awal"}: {p.dikerjakan} pcs diperiksa · <span className="text-emerald-300">{p.lulus ?? "?"} lulus</span>{p.reject ? <span className="text-red-300"> · {p.reject} reject</span> : ""}{p.total_lulus != null && p.hasil === "LULUS" ? <b> · total lulus SPK {p.total_lulus} pcs</b> : ""}</li>
                        ))}
                      </ul>
                    )}
                    {t.qc?.length > 0 && t.qc.map((q, j) => (
                      <div key={j} className={`text-xs ${q.result === "REJECTED" ? "text-red-300" : "text-emerald-300"}`}>
                        QC {q.result === "REJECTED" ? "REJECT" : "PASS"} {waktu(q.qc_at)} {q.qc_by_username ? `(${q.qc_by_username})` : ""}{q.reason ? ` — ${q.reason}` : ""}
                        {q.pos_penyebab ? ` · penyebab ${namaPos(q.pos_penyebab, petaPos)}` : ""}{q.qty_reject ? ` · ${q.qty_reject} pcs` : ""}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function RiwayatProduksi() {
  const { user } = useAuth();
  const isAdmin = ["admin", "manager"].includes(user?.role);  // selisih qty: Owner + Manager (2026-10-12)
  const [f, setF] = React.useState({ q: "", jenis: "", status: "", dari: "", sampai: "" });
  const [rows, setRows] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [buka, setBuka] = React.useState(null);

  const load = React.useCallback(async () => {
    setBusy(true); setError(null);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    try { setRows(await apiRiwayatProduksi(params)); } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  }, [f]);
  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Addendum 2: the owner's warning links straight here (?spk=<id>)
  const [params] = useSearchParams();
  React.useEffect(() => { const id = params.get("spk"); if (id) setBuka(id); }, [params]);

  return (
    <div data-testid="riwayat-produksi-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Riwayat Produksi</h1>
          <p className="text-sm text-slate-400 mt-1">Semua SPK (Digital Printing, Jersey, DTF, Custom) dengan progres per pos sampai pengambilan. Hijau = selesai, biru = sedang diproses, abu-abu = belum.</p>
        </div>
        {isAdmin && <SelisihQty onOpen={setBuka} testid="riwayat-selisih-owner" />}
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap items-end gap-2">
          <div className="relative flex-1 min-w-[14rem]">
            <Search size={14} className="absolute left-3 top-3 text-slate-500" />
            <input data-testid="riwayat-cari" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })}
              placeholder={user?.role === "desain" ? "No. SPK / faktur / customer" : "No. SPK / faktur / customer / PIC / WA"} className={`${inputCls} w-full pl-8`} />
          </div>
          <select data-testid="riwayat-jenis" value={f.jenis} onChange={(e) => setF({ ...f, jenis: e.target.value })} className={inputCls}>
            {JENIS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <select data-testid="riwayat-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inputCls}>
            <option value="">Semua status</option>{STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          <input type="date" value={f.dari} onChange={(e) => setF({ ...f, dari: e.target.value })} className={inputCls} title="Tanggal SPK dari" />
          <input type="date" value={f.sampai} onChange={(e) => setF({ ...f, sampai: e.target.value })} className={inputCls} title="sampai" />
          <button data-testid="riwayat-tampilkan" type="submit" className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Tampilkan</button>
        </form>
        {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
        {buka && <Detail key={buka} spkId={buka} onClose={() => setBuka(null)} />}
        {rows?.length === 0 && <p className="text-slate-500">Tidak ada SPK yang cocok.</p>}
        {rows?.length >= 300 && <p className="text-xs text-slate-500">Menampilkan 300 SPK terbaru; persempit dengan tanggal atau pencarian.</p>}
        <div className="space-y-2">
          {rows?.map((r) => (
            <button key={r.spk_id} data-testid={`riwayat-row-${r.nomor_spk}`} onClick={() => setBuka(r.spk_id)}
              className={`w-full text-left rounded-xl border p-3 space-y-2 bg-slate-900/60 hover:border-amber-500/40 ${r.lewat_rencana ? "border-red-500/40" : "border-slate-800"}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-mono text-amber-300">{r.nomor_spk}</span>
                <span className="text-xs text-slate-500">{r.nomor_faktur}</span>
                <span className="text-xs rounded border border-slate-700 px-1.5">{r.jenis}{r.mesin ? ` · ${r.mesin}` : ""}</span>
                <span className={`rounded px-2 py-0.5 text-[11px] font-mono ${STATUS_CLS[r.status]}`}>{r.status}</span>
                <span className="text-slate-200">{r.customer}{r.tim ? ` · ${r.tim}` : ""}{r.pic ? ` · PIC ${r.pic}` : ""}</span>
                {r.status_bayar && <span className={`text-xs ${BAYAR_CLS[r.status_bayar] || ""}`}>{r.status_bayar}</span>}
                {r.ada_selisih_qty && <span className="text-xs text-red-300">⚠ selisih qty</span>}
                <span className="ml-auto text-xs text-slate-400">SPK {waktu(r.tanggal_spk)} · rencana {rencanaTeks(r)}
                  {r.selesai_aktual ? ` · selesai ${waktu(r.selesai_aktual)}` : ""}{r.lewat_rencana ? " · lewat rencana" : ""}</span>
              </div>
              <Progres steps={r.progres} />
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}
